import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ArrowUpIcon from '../assets/nav-arrow-up.svg';
import ArrowDownIcon from '../assets/nav-arrow-down.svg';
import CustomButton from '../reusable/CustomButton';
import './SideBarNavigationLink.css';

export interface ChildLink {
  label: string;
  path: string;
  children: ChildLink[];
  disabled: boolean;
}
interface SideBarNavigationLinkProps {
  path: string;
  icon?: string;
  label: string;
  children: ChildLink[];
  isExpandable?: boolean;
  currentPath: string;
  isNavigationClosed: boolean;
  handleCloseSideDrawer?: () => void;
  disabled?: boolean;
  activePathPatterns?: string[];
}

const SideBarNavigationLink = ({
  path,
  icon,
  label,
  children,
  isExpandable,
  currentPath,
  isNavigationClosed,
  handleCloseSideDrawer,
  activePathPatterns = [],
}: SideBarNavigationLinkProps) => {
  const location = useLocation();

  const [isExpanded, setIsExpanded] = useState(false);

  const isChildActive = (childPath: string, currentPathValue: string) => {
    if (currentPathValue === childPath) return true;
    if (
      (childPath === '/transcript-selection' || childPath === '/transcripts') &&
      currentPathValue.startsWith('/transcripts')
    )
      return true;
    if (childPath === '/school-reports' && currentPathValue.startsWith('/school-reports')) return true;
    if (childPath === '/student-voice-for-reports' && currentPathValue.startsWith('/student-voice-for-reports'))
      return true;
    if (
      childPath === '/survey-reports' &&
      (currentPathValue.startsWith('/survey-reports') || currentPathValue.startsWith('/survey-response-dashboard'))
    )
      return true;
    if (
      childPath === '/focus-group-reports-lead-facilitator' &&
      currentPathValue.startsWith('/focus-group-reports-lead-facilitator')
    )
      return true;
    return false;
  };

  useEffect(() => {
    if (path === '/data-qc' && path === location.pathname) {
      setIsExpanded(true);
    } else if (path === '/dashboard' && isExpandable && children.length > 0) {
      const hasActiveChild = children.some((child) => isChildActive(child.path, location.pathname));
      if (hasActiveChild || location.pathname === '/dashboard') {
        setIsExpanded(true);
      }
    }
  }, [location, path, isExpandable, children]);

  const handleExpandSubLinks = () => {
    setIsExpanded(!isExpanded);
  };

  const isActive = () => {
    const currentPathValue = location.pathname;

    if (isExpandable && children.length > 0) {
      const hasActiveChild = children.some((child) => isChildActive(child.path, currentPathValue));
      if (hasActiveChild) {
        return false;
      }
    }

    if (currentPathValue === path) return true;

    return activePathPatterns.some((pattern) => currentPathValue.startsWith(pattern));
  };

  return (
    <>
      <li data-testid="navigation-link-container" className={`navigation-link-container ${isActive() ? 'active' : ''}`}>
        <NavLink className="navigation-link" to={path} onClick={handleCloseSideDrawer}>
          <img className="navigation-link-icon" alt="link icon" src={icon} />
          <span className="navigation-link-text">{label}</span>
        </NavLink>
        {isExpandable && !isNavigationClosed ? (
          <CustomButton variant="clear" buttonAction={handleExpandSubLinks} sx={{ padding: '0', minWidth: '0' }}>
            <img
              id={label}
              alt={isExpanded ? 'open navigation sub menu' : 'close navigation sub menu'}
              src={isExpanded ? ArrowUpIcon : ArrowDownIcon}
              className="arrow-icon"
            />
          </CustomButton>
        ) : null}
      </li>

      {isExpanded && children.length > 0 && (
        <li data-testid="navigation-sub-links-container" className="navigation-sub-links-container">
          <ul className="navigation-sub-links-list">
            {children.map(({ label, path: childPath, disabled }, index) => {
              const checkChildActive = () => {
                return isChildActive(childPath, location.pathname);
              };

              return (
                <li key={index}>
                  {disabled ? (
                    <div className={`navigation-link-container ${disabled ? 'disabled' : ''}`}>{label}</div>
                  ) : (
                    <NavLink
                      to={childPath}
                      className={`navigation-link-container ${checkChildActive() ? 'active' : ''}`}
                      onClick={handleCloseSideDrawer}
                    >
                      {label}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </li>
      )}
    </>
  );
};

export default SideBarNavigationLink;
